import * as yup from 'yup';
import {
    ExtensionStatusHistory,
    ProfessionAddOnTrack,
    ProfessionsConcentrationDto,
    ProgramExtension,
    ProgramExtensionCreateOrUpdate,
} from '../../../common/Types';
import {
    ButtonGroup,
    Card,
    CardBody,
    Form,
    FormGroup,
    Input,
    Label,
    Modal,
    ModalBody,
    ModalFooter,
    ModalHeader,
} from 'reactstrap';
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import { Button } from '../../../components';
import { ExtensionStatus, ExtensionType } from '../../../common/Data';
import moment from 'moment';
import { programService } from '../../../services/ProgramService';
import { toast } from 'react-toastify';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { AmsPermissionClaimType, authService } from '../../../services/Auth';
import Skeleton from 'react-loading-skeleton';

interface ExtensionProps {
    programId: number;
    professionId: number;
    professionConcentrations: ProfessionsConcentrationDto[];
    professionAddOnTracks: ProfessionAddOnTrack[];
    onSaved?: () => void;
}

// Form data type with string dates for HTML inputs
interface ProgramExtensionFormData {
    id: number;
    programId: number;
    extensionTypeId: number;
    addOnTrackId: number | null;
    concentrationId: number | null;
    dateOfInitialApproval: string | null;
    statusId: number;
    effectiveDate: string | null;
    isDeleted: boolean;
}

// Create schema for form validation
const createProgramExtensionSchema = (extensionType?: ExtensionType) => {
    return yup.object({
        id: yup.number().required().default(0),
        programId: yup.number().required(),
        extensionTypeId: yup.number().required(),
        addOnTrackId: yup
            .number()
            .nullable()
            .test('addOnTrackId-required', 'Add-On Track is required', function (value) {
                if (extensionType === ExtensionType['Add On']) {
                    return value != null && value >= 1;
                }
                return true;
            }),
        concentrationId: yup
            .number()
            .nullable()
            .test('concentrationId-required', 'Concentration is required', function (value) {
                if (extensionType === ExtensionType.Concentration) {
                    return value != null && value >= 1;
                }
                return true;
            }),
        dateOfInitialApproval: yup.string().required('Approval date is required').nullable(),
        statusId: yup
            .number()
            .required('Status is required')
            .min(1, 'Status is required')
            .typeError('A valid status is required'),
        effectiveDate: yup.string().required('Effective date is required').nullable(),
        isDeleted: yup.boolean().required().default(false),
    });
};

const Extensions: React.FC<ExtensionProps> = ({
    programId,
    professionId,
    professionConcentrations,
    professionAddOnTracks,
    onSaved,
}) => {
    const [extensionType, setExtensionType] = useState<ExtensionType>();
    const [programExtensions, setProgramExtensions] = useState<ProgramExtension[]>([]);
    const [loading, setLoading] = useState(true);
    const [selectedExtension, setSelectedExtension] = useState<ProgramExtension | undefined>();
    const [extensionCreateOrUpdate, setExtensionCreateOrUpdate] = useState<ProgramExtensionCreateOrUpdate | null>(null);
    const [showModal, setShowModal] = useState(false);
    const [showDateConflictModal, setShowDateConflictModal] = useState(false);
    const [pendingValues, setPendingValues] = useState<ProgramExtensionFormData | null>(null);
    const [conflictingStatusId, setConflictingStatusId] = useState<number | null>(null);
    const [showDeleteConfirmModal, setShowDeleteConfirmModal] = useState(false);
    const [extensionToDelete, setExtensionToDelete] = useState<ProgramExtension | null>(null);

    // Memoize permission check
    const hasAdminPermission = useMemo(() => authService.hasPermission(AmsPermissionClaimType, 'systemadmin'), []);

    // Memoize schema based on extension type
    const validationSchema = useMemo(() => createProgramExtensionSchema(extensionType), [extensionType]);

    const {
        control,
        reset,
        handleSubmit,
        formState: { errors, isDirty },
    } = useForm<ProgramExtensionFormData>({
        resolver: yupResolver(validationSchema) as any,
        mode: 'onChange',
        defaultValues: {
            id: 0,
            programId: 0,
            extensionTypeId: 0,
            addOnTrackId: null,
            concentrationId: null,
            dateOfInitialApproval: null,
            statusId: 0,
            effectiveDate: null,
            isDeleted: false,
        },
    });

    // Load program extensions
    useEffect(() => {
        const loadProgramExtensions = async () => {
            if (!programId) return;

            setLoading(true);
            try {
                const results = await programService.getProgramExtensions(programId);
                setProgramExtensions(results);
            } catch (error) {
                toast.error('Error loading program extensions');
                console.error('Failed to load program extensions:', error);
            } finally {
                setLoading(false);
            }
        };

        loadProgramExtensions();
    }, [programId]);

    // Set extension type based on available data
    useEffect(() => {
        if (professionAddOnTracks.length > 0) {
            setExtensionType(ExtensionType['Add On']);
        } else if (professionConcentrations.length > 0) {
            setExtensionType(ExtensionType.Concentration);
        }
    }, [professionAddOnTracks, professionConcentrations]);

    // Reset form when modal opens/closes
    useEffect(() => {
        if (!showModal) {
            setExtensionCreateOrUpdate(null);
            return;
        }

        if (!extensionCreateOrUpdate) return;

        const formData: ProgramExtensionFormData = {
            id: extensionCreateOrUpdate.id || 0,
            programId: programId,
            extensionTypeId: extensionType || 0,
            dateOfInitialApproval: extensionCreateOrUpdate.dateOfInitialApproval
                ? moment.utc(extensionCreateOrUpdate.dateOfInitialApproval).format('YYYY-MM-DD')
                : moment().format('YYYY-MM-DD'),
            effectiveDate: extensionCreateOrUpdate.effectiveDate
                ? moment.utc(extensionCreateOrUpdate.effectiveDate).format('YYYY-MM-DD')
                : moment().format('YYYY-MM-DD'),
            addOnTrackId: extensionCreateOrUpdate.addOnTrackId || null,
            concentrationId: extensionCreateOrUpdate.concentrationId || null,
            statusId: extensionCreateOrUpdate.statusId || 0,
            isDeleted: false,
        };

        reset(formData);
    }, [showModal, extensionCreateOrUpdate, reset, programId, extensionType]);

    const getActiveStatus = useCallback((extension: ProgramExtension) => {
        return extension?.extensionStatusHistory?.find((sh) => sh.active);
    }, []);

    const toggleModal = useCallback(() => {
        setShowModal((prev) => !prev);
    }, []);

    const startNewExtension = useCallback(() => {
        const newExtension: ProgramExtensionCreateOrUpdate = {
            id: 0,
            programId,
            extensionTypeId: extensionType,
            addOnTrackId: null,
            concentrationId: null,
            statusId: 0,
            dateOfInitialApproval: null,
            effectiveDate: null,
            isDeleted: false,
        };

        setExtensionCreateOrUpdate(newExtension);
        toggleModal();
    }, [programId, extensionType, toggleModal]);

    const startUpdateExtension = useCallback(
        (extension: ProgramExtension) => {
            const activeStatus = getActiveStatus(extension);
            const updateExtension: ProgramExtensionCreateOrUpdate = {
                id: extension.id,
                programId: extension.programId,
                extensionTypeId: extension.extensionTypeId,
                addOnTrackId: extension.addOnTrackId || null,
                concentrationId: extension.concentrationId || null,
                dateOfInitialApproval: extension.dateOfInitialApproval || null,
                statusId: activeStatus?.extensionStatusId || 0,
                effectiveDate: null, // Always default to current date for new status
                isDeleted: false,
            };

            setExtensionCreateOrUpdate(updateExtension);
            toggleModal();
        },
        [getActiveStatus, toggleModal],
    );

    const cancelUpdateExtension = useCallback(() => {
        setExtensionCreateOrUpdate(null);
        toggleModal();
    }, [toggleModal]);

    const submitExtension = useCallback(
        async (values: ProgramExtensionFormData, forceReplace: boolean = false) => {
            // Skip if no changes for existing records (but not for deletes)
            if (values.id > 0 && !isDirty && !values.isDeleted) {
                toggleModal();
                return;
            }

            const toastId = toast.info('Saving...');

            try {
                if (values.id === 0 || values.isDeleted) {
                    // Creating a new extension OR deleting - use the extension endpoint
                    const apiData: ProgramExtensionCreateOrUpdate = {
                        id: values.id,
                        programId: values.programId,
                        extensionTypeId: values.extensionTypeId,
                        dateOfInitialApproval: values.dateOfInitialApproval ? new Date(values.dateOfInitialApproval) : null,
                        effectiveDate: values.effectiveDate ? new Date(values.effectiveDate) : null,
                        addOnTrackId: values.addOnTrackId && values.addOnTrackId > 0 ? values.addOnTrackId : null,
                        concentrationId:
                            values.concentrationId && values.concentrationId > 0 ? values.concentrationId : null,
                        statusId: values.statusId,
                        isDeleted: values.isDeleted,
                    };

                    await programService.saveProgramExtension(apiData);
                } else {
                    // Updating extension status - check for existing status with same date
                    const extension = programExtensions.find((pe) => pe.id === values.id);
                    // Form date is already in YYYY-MM-DD format from HTML date input
                    const formDate = values.effectiveDate || moment().format('YYYY-MM-DD');

                    const existingStatusForDate = extension?.extensionStatusHistory?.find((sh) => {
                        if (sh.isDeleted) return false;
                        // Convert API date to same format for comparison
                        const statusDate = sh.effectiveDate
                            ? moment.utc(sh.effectiveDate).format('YYYY-MM-DD')
                            : null;
                        return statusDate === formDate;
                    });

                    // If there's an existing status for this date and we're not forcing replace, prompt user
                    if (existingStatusForDate && !forceReplace) {
                        setPendingValues(values);
                        setConflictingStatusId(existingStatusForDate.id);
                        setShowDateConflictModal(true);
                        toast.dismiss(toastId);
                        return;
                    }

                    // Create or update status history record
                    const statusHistoryData: ExtensionStatusHistory = {
                        id: forceReplace && existingStatusForDate ? existingStatusForDate.id : 0,
                        programExtensionId: values.id,
                        extensionStatusId: values.statusId,
                        effectiveDate: values.effectiveDate ? new Date(values.effectiveDate) : new Date(),
                        active: true,
                        isDeleted: false,
                        dateCreated: new Date(),
                        lastEdited: new Date(),
                        createdBy: '',
                        editedBy: '',
                    };

                    await programService.saveExtensionStatusHistory(statusHistoryData);
                }

                toast.update(toastId, {
                    render: 'Update successful.',
                    type: 'success',
                    autoClose: 3000,
                });

                // Reload extensions
                const results = await programService.getProgramExtensions(programId);
                setProgramExtensions(results);
                
                // Notify parent component of changes
                onSaved?.();

                if (!values.isDeleted) {
                    toggleModal();
                }
            } catch (error) {
                console.error('Save error:', error);
                toast.update(toastId, {
                    render: 'Error updating Program.',
                    type: 'error',
                    autoClose: 5000,
                });
            }
        },
        [isDirty, programId, programExtensions, toggleModal],
    );

    const handleReplaceStatus = useCallback(() => {
        if (pendingValues) {
            setShowDateConflictModal(false);
            submitExtension(pendingValues, true);
            setPendingValues(null);
            setConflictingStatusId(null);
        }
    }, [pendingValues, submitExtension]);

    const handleCancelReplace = useCallback(() => {
        setShowDateConflictModal(false);
        setPendingValues(null);
        setConflictingStatusId(null);
    }, []);

    const promptDeleteExtension = useCallback((extension: ProgramExtension) => {
        setExtensionToDelete(extension);
        setShowDeleteConfirmModal(true);
    }, []);

    const confirmDeleteExtension = useCallback(() => {
        if (!extensionToDelete) return;

        const deletedExtension: ProgramExtensionFormData = {
            id: extensionToDelete.id,
            programId: extensionToDelete.programId,
            extensionTypeId: extensionToDelete.extensionTypeId || 0,
            addOnTrackId: null,
            concentrationId: null,
            dateOfInitialApproval: null,
            statusId: 0,
            effectiveDate: null,
            isDeleted: true,
        };
        setShowDeleteConfirmModal(false);
        setExtensionToDelete(null);
        submitExtension(deletedExtension, false);
    }, [extensionToDelete, submitExtension]);

    const cancelDeleteExtension = useCallback(() => {
        setShowDeleteConfirmModal(false);
        setExtensionToDelete(null);
    }, []);

    const handleToggleExtension = useCallback((extension: ProgramExtension) => {
        setSelectedExtension((prev) => (prev?.id === extension.id ? undefined : extension));
    }, []);

    // Early return if no extensions available
    if (!professionConcentrations.length && !professionAddOnTracks.length) {
        return null;
    }

    const extensionTitle = extensionType === ExtensionType['Add On'] ? 'Add-On Tracks' : 'Concentrations';
    const singularTitle = extensionType === ExtensionType['Add On'] ? 'Add-On Track' : 'Concentration';

    return (
        <>
            <Card className="d-flex flex-grow-1">
                <CardBody>
                    <h4 className="card-title d-flex align-items-center justify-content-between">
                        <span>{extensionTitle}</span>
                        {hasAdminPermission && (
                            <Button
                                type="button"
                                color="primary"
                                className="btn-icon"
                                onClick={startNewExtension}
                                aria-label={`Add new ${singularTitle}`}
                            >
                                <i className="mdi mdi-plus" />
                            </Button>
                        )}
                    </h4>
                </CardBody>
                <div className="table-responsive" style={{ maxHeight: '350px', overflow: 'auto' }}>
                    <table className="table mb-0">
                        <thead>
                            <tr>
                                <th style={{ width: '6%' }}></th>
                                <th>Name</th>
                                <th>Date of Initial Approval</th>
                                <th>Status</th>
                                <th>Effective Date</th>
                                <th></th>
                            </tr>
                        </thead>
                        <tbody>
                            {loading ? (
                                <tr>
                                    <td colSpan={6}>
                                        <Skeleton count={5} />
                                    </td>
                                </tr>
                            ) : programExtensions.length > 0 ? (
                                programExtensions.map((pe) => {
                                    const isExpanded = selectedExtension?.id === pe.id;
                                    const activeStatusHistory = pe.extensionStatusHistory?.find((sh) => sh.active);

                                    return (
                                        <React.Fragment key={pe.id}>
                                            <tr>
                                                <td>
                                                    <Button
                                                        onClick={() => handleToggleExtension(pe)}
                                                        color="white"
                                                        aria-label={isExpanded ? 'Collapse' : 'Expand'}
                                                    >
                                                        <i
                                                            className={`mdi mdi-chevron-${isExpanded ? 'down' : 'right'}`}
                                                        />
                                                    </Button>
                                                </td>
                                                <td>
                                                    {extensionType === ExtensionType.Concentration
                                                        ? professionConcentrations.find(
                                                              (pc) => pc.concentrationId === pe.concentrationId,
                                                          )?.title || 'Unknown'
                                                        : professionAddOnTracks.find(
                                                              (paot) => paot.addOnTrackId === pe.addOnTrackId,
                                                          )?.title || 'Unknown'}
                                                </td>
                                                <td>
                                                    {pe.dateOfInitialApproval
                                                        ? moment.utc(pe.dateOfInitialApproval).format('MM/DD/YYYY')
                                                        : 'Not Set'}
                                                </td>
                                                <td>
                                                    {pe.activeStatus ? ExtensionStatus[pe.activeStatus] : 'Not Set'}
                                                </td>
                                                <td>
                                                    {activeStatusHistory?.effectiveDate
                                                        ? moment
                                                              .utc(activeStatusHistory.effectiveDate)
                                                              .format('MM/DD/YYYY')
                                                        : ''}
                                                </td>
                                                <td className="text-right">
                                                    <ButtonGroup>
                                                        <Button
                                                            color="success"
                                                            className="btn-sm"
                                                            onClick={() => startUpdateExtension(pe)}
                                                            aria-label={`Edit ${singularTitle}`}
                                                        >
                                                            <i className="mdi mdi-pencil" />
                                                        </Button>
                                                    </ButtonGroup>
                                                </td>
                                            </tr>
                                            {isExpanded &&
                                                pe.extensionStatusHistory &&
                                                pe.extensionStatusHistory.length > 0 &&
                                                pe.extensionStatusHistory.map((sh) => (
                                                    <tr key={sh.id} style={{ backgroundColor: '#f8f9fa' }}>
                                                        <td></td>
                                                        <td></td>
                                                        <td></td>
                                                        <td>{ExtensionStatus[sh.extensionStatusId || 0]}</td>
                                                        <td>{moment.utc(sh.effectiveDate).format('MM/DD/YYYY')}</td>
                                                        <td></td>
                                                    </tr>
                                                ))}
                                        </React.Fragment>
                                    );
                                })
                            ) : (
                                <tr>
                                    <td colSpan={6}>
                                        <label>
                                            {`Click the '+' button to add a${
                                                extensionType === ExtensionType.Concentration
                                                    ? ' concentration'
                                                    : 'n add-on track'
                                            } for this program`}
                                        </label>
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </Card>

            <Modal isOpen={showModal} toggle={toggleModal} centered>
                <ModalHeader>
                    {extensionCreateOrUpdate?.id ? 'Edit' : 'New'} Program {singularTitle}
                </ModalHeader>
                <Form noValidate onSubmit={handleSubmit((data) => submitExtension(data, false))}>
                    <ModalBody>
                        {extensionType === ExtensionType['Add On'] && (
                            <FormGroup>
                                <Label>Add-On Track</Label>
                                <Controller
                                    name="addOnTrackId"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            type="select"
                                            {...field}
                                            value={field.value || 0}
                                            onChange={(e) =>
                                                field.onChange(e.target.value ? Number(e.target.value) : null)
                                            }
                                            disabled={
                                                !!(extensionCreateOrUpdate?.id && extensionCreateOrUpdate?.addOnTrackId)
                                            }
                                        >
                                            <option value={0}>Select...</option>
                                            {professionAddOnTracks
                                                .filter((paot) =>
                                                    // Allow if this is the currently selected value (for editing)
                                                    paot.addOnTrackId === field.value ||
                                                    // Or if no existing program extension with this add-on (or existing one is Removed)
                                                    !programExtensions.some(
                                                        (pe) =>
                                                            pe.addOnTrackId === paot.addOnTrackId &&
                                                            !pe.isDeleted &&
                                                            pe.activeStatus !== ExtensionStatus.Removed
                                                    )
                                                )
                                                .map((paot) => (
                                                    <option key={paot.addOnTrackId} value={paot.addOnTrackId}>
                                                        {paot.title}
                                                    </option>
                                                ))}
                                        </Input>
                                    )}
                                />
                                {errors.addOnTrackId && (
                                    <span className="text-danger">{errors.addOnTrackId.message}</span>
                                )}
                            </FormGroup>
                        )}

                        {extensionType === ExtensionType.Concentration && (
                            <FormGroup>
                                <Label>Concentration</Label>
                                <Controller
                                    name="concentrationId"
                                    control={control}
                                    render={({ field }) => (
                                        <Input
                                            type="select"
                                            {...field}
                                            value={field.value || 0}
                                            onChange={(e) =>
                                                field.onChange(e.target.value ? Number(e.target.value) : null)
                                            }
                                            disabled={
                                                !!(
                                                    extensionCreateOrUpdate?.id &&
                                                    extensionCreateOrUpdate?.concentrationId
                                                )
                                            }
                                        >
                                            <option value={0}>Select...</option>
                                            {professionConcentrations
                                                .filter((pc) =>
                                                    // Allow if this is the currently selected value (for editing)
                                                    pc.concentrationId === field.value ||
                                                    // Must not be deleted from profession
                                                    (!pc.isDeleted &&
                                                        // And no existing program extension with this concentration (or existing one is Removed)
                                                        !programExtensions.some(
                                                            (pe) =>
                                                                pe.concentrationId === pc.concentrationId &&
                                                                !pe.isDeleted &&
                                                                pe.activeStatus !== ExtensionStatus.Removed
                                                        ))
                                                )
                                                .map((pc) => (
                                                    <option key={pc.concentrationId} value={pc.concentrationId}>
                                                        {pc.title}
                                                    </option>
                                                ))}
                                        </Input>
                                    )}
                                />
                                {errors.concentrationId && (
                                    <span className="text-danger">{errors.concentrationId.message}</span>
                                )}
                            </FormGroup>
                        )}

                        <FormGroup>
                            <Label>Date of Initial Approval</Label>
                            <Controller
                                name="dateOfInitialApproval"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        type="date"
                                        {...field}
                                        value={field.value || ''}
                                        onChange={(e) => field.onChange(e.target.value || null)}
                                        disabled={
                                            !!(
                                                extensionCreateOrUpdate?.id &&
                                                extensionCreateOrUpdate?.dateOfInitialApproval
                                            )
                                        }
                                    />
                                )}
                            />
                            {errors.dateOfInitialApproval && (
                                <span className="text-danger">{errors.dateOfInitialApproval.message}</span>
                            )}
                        </FormGroup>

                        <FormGroup>
                            <Label>Status</Label>
                            <Controller
                                name="statusId"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        type="select"
                                        {...field}
                                        value={field.value || 0}
                                        onChange={(e) => field.onChange(Number(e.target.value))}
                                    >
                                        <option value={0} disabled>
                                            Select Status...
                                        </option>
                                        {Object.keys(ExtensionStatus)
                                            .filter((key) => !isNaN(Number(key)))
                                            .map((key) => (
                                                <option key={key} value={key}>
                                                    {ExtensionStatus[+key]}
                                                </option>
                                            ))}
                                    </Input>
                                )}
                            />
                            {errors.statusId && <span className="text-danger">{errors.statusId.message}</span>}
                        </FormGroup>

                        <FormGroup>
                            <Label>Effective Date</Label>
                            <Controller
                                name="effectiveDate"
                                control={control}
                                render={({ field }) => (
                                    <Input
                                        type="date"
                                        {...field}
                                        value={field.value || ''}
                                        onChange={(e) => field.onChange(e.target.value || null)}
                                    />
                                )}
                            />
                            {errors.effectiveDate && (
                                <span className="text-danger">{errors.effectiveDate.message}</span>
                            )}
                        </FormGroup>
                    </ModalBody>
                    <ModalFooter>
                        <Button color="primary" className="mr-2" type="submit">
                            Save
                        </Button>
                        <Button color="link" onClick={cancelUpdateExtension} type="button">
                            Cancel
                        </Button>
                    </ModalFooter>
                </Form>
            </Modal>

            <Modal isOpen={showDateConflictModal} toggle={handleCancelReplace} centered>
                <ModalHeader>Status Already Exists for This Date</ModalHeader>
                <ModalBody>
                    <p>
                        A status already exists for{' '}
                        <strong>
                            {pendingValues?.effectiveDate
                                ? moment.utc(pendingValues.effectiveDate).format('MM/DD/YYYY')
                                : 'this date'}
                        </strong>
                        .
                    </p>
                    <p>Would you like to replace the existing status, or adjust the date?</p>
                </ModalBody>
                <ModalFooter>
                    <Button color="primary" className="mr-2" onClick={handleReplaceStatus}>
                        Replace Status
                    </Button>
                    <Button color="secondary" onClick={handleCancelReplace}>
                        Adjust Date
                    </Button>
                </ModalFooter>
            </Modal>

            <Modal isOpen={showDeleteConfirmModal} toggle={cancelDeleteExtension} centered>
                <ModalHeader>Confirm Delete</ModalHeader>
                <ModalBody>
                    <p>
                        Are you sure you want to remove this{' '}
                        {extensionType === ExtensionType['Add On'] ? 'add-on track' : 'concentration'}?
                    </p>
                    <p className="text-danger mb-0">
                        <strong>This action cannot be undone.</strong>
                    </p>
                </ModalBody>
                <ModalFooter>
                    <Button color="danger" className="mr-2" onClick={confirmDeleteExtension}>
                        Delete
                    </Button>
                    <Button color="secondary" onClick={cancelDeleteExtension}>
                        Cancel
                    </Button>
                </ModalFooter>
            </Modal>
        </>
    );
};

export default Extensions;
